import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import VSCodeEditor from '../../components/UI/VSCodeEditor';
import { createApplicationTool, getApplicationById } from '../../services/applications/applicationsService';
import { ToolDto, ToolStatus, ToolType, ApplicationDto } from '../../services/applications/types';

const ToolCreate: React.FC = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [formData, setFormData] = useState<Partial<ToolDto>>({
    name: '',
    description: '',
    type: ToolType.FUNCTION,
    status: ToolStatus.ACTIVE,
    files: []
  });

  useEffect(() => {
    if (applicationId && auth.user) {
      fetchApplication();
    }
  }, [applicationId, auth.user]);

  const fetchApplication = async () => {
    try {
      const data = await getApplicationById(auth.user || null, applicationId!);
      setApplication(data);
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Failed to load application data.');
    } finally {
      setIsLoadingApp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type || !formData.status) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    setIsSaving(true);
    setError(null);

    try {
      // Convert files to script for backend compatibility
      const mainFile = formData.files?.find(f => f.name === 'main.py');
      const toolData = {
        ...formData,
        script: mainFile?.content || ''
      };
      
      await createApplicationTool(auth.user || null, applicationId!, toolData as ToolDto);
      navigate(`/applications/${applicationId}/tools`);
    } catch (err) {
      console.error('Error creating tool:', err);
      setError('Failed to create tool. Please try again.');
    } finally {
      setIsLoading(false);
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof ToolDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case ToolStatus.ACTIVE:
        return 'text-green-600 bg-green-50 border-green-200';
      case ToolStatus.INACTIVE:
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case ToolStatus.DEPRECATED:
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (isLoadingApp) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <PageHeader
            title={`Create Function - ${application?.name || 'Loading...'}`}
            breadcrumbs={[
              { label: "Dashboard", to: "/" },
              { label: "Applications", to: "/applications" },
              { label: application?.name || 'Application', to: `/applications/${applicationId}` },
              { label: "Functions", to: `/applications/${applicationId}/tools` },
              { label: "Create" }
            ]}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex h-[calc(100vh-88px)]">
        {/* Left Panel - Form */}
        <div className="w-[400px] bg-white border-r border-gray-200 flex flex-col">
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-4">
              {/* Header Actions */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <i className="fas fa-cog mr-2 text-blue-500"></i>
                  Configuration
                </h2>
                {hasChanges && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                    <i className="fas fa-circle text-amber-500 text-[8px] mr-1"></i>
                    Unsaved
                  </span>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-start">
                  <i className="fas fa-exclamation-circle mr-2 mt-0.5 text-sm"></i>
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Function Details */}
              <div className="space-y-4">

                <div>
                  <FormInput
                    label="Function Name"
                    name="name"
                    value={formData.name || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                    required
                    placeholder="e.g., calculate_metrics"
                    className="font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-500">Use snake_case naming</p>
                </div>

                <div>
                  <FormInput
                    label="Description"
                    name="description"
                    value={formData.description || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('description', e.target.value)}
                    placeholder="Brief description"
                  />
                </div>

                <div>
                  <FormSelect
                    label="Status"
                    name="status"
                    value={formData.status || ''}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('status', e.target.value)}
                    required
                    options={[
                      { value: ToolStatus.ACTIVE, label: 'Active' },
                      { value: ToolStatus.INACTIVE, label: 'Inactive' },
                      { value: ToolStatus.DEPRECATED, label: 'Deprecated' }
                    ]}
                  />
                  {formData.status && (
                    <div className={`mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(formData.status)}`}>
                      <i className="fas fa-circle text-[8px] mr-1.5"></i>
                      {formData.status}
                    </div>
                  )}
                </div>
              </div>

              {/* Code Guidelines */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="fas fa-book mr-2 text-gray-500"></i>
                  Guidelines
                </h3>
                <div className="space-y-1.5 text-xs text-gray-600">
                  <div className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-1.5 mt-0.5 text-xs"></i>
                    <span>Main function: <code className="bg-gray-200 px-1 rounded text-xs">main(context)</code></span>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-1.5 mt-0.5 text-xs"></i>
                    <span>Return dict with status, message, data</span>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-1.5 mt-0.5 text-xs"></i>
                    <span>Add deps to requirements.txt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions - Fixed at bottom */}
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="space-y-3">
              <Button
                type="submit"
                variant="primary"
                disabled={isLoading}
                className="w-full justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Function...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus-circle mr-2"></i>
                    Create Function
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/applications/${applicationId}/tools`)}
                className="w-full justify-center"
              >
                <i className="fas fa-times mr-2"></i>
                Cancel
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Editor */}
        <div className="flex-1 bg-gray-900 flex flex-col">
          {/* Editor Header */}
          <div className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h3 className="text-white font-medium flex items-center text-sm">
                <i className="fas fa-code mr-2 text-blue-400"></i>
                Code Editor
              </h3>
            </div>
            <div className="flex items-center space-x-3 text-xs">
              {isSaving && (
                <span className="text-green-400 flex items-center">
                  <i className="fas fa-check-circle mr-1"></i>
                  Saving...
                </span>
              )}
              <span className="text-gray-400">
                {formData.files?.length || 0} files
              </span>
            </div>
          </div>

          {/* VSCode Editor */}
          <div className="flex-1 overflow-hidden">
            <VSCodeEditor
              files={formData.files || []}
              onFilesChange={(files) => handleChange('files', files)}
              height="100%"
              theme="vs-dark"
            />
          </div>

          {/* Editor Status Bar */}
          <div className="bg-gray-800 border-t border-gray-700 px-4 py-1 flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <i className="fas fa-file-code mr-1"></i>
                Python
              </span>
              <span>UTF-8</span>
              <span>4 Spaces</span>
            </div>
            <div className="flex items-center">
              <i className="fas fa-keyboard mr-1"></i>
              Ready
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ToolCreate;